import { useQuery } from '@tanstack/react-query'
import { adminDashboardService, type SystemMetrics, type TenantOverview, type RevenueMetrics, type ResourceMetrics, type Activity } from '@/services/admin-dashboard.service'

/**
 * Composite hook for dashboard queries
 * All queries return extracted data (T) from StandardResponse<T>
 */
export function useDashboardQueries() {
  /**
   * Query: System metrics
   * Returns: SystemMetrics
   */
  const systemMetrics = useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: adminDashboardService.getSystemMetrics,
    refetchInterval: 30000, // 30 seconds
  })

  /**
   * Query: Tenant overview
   * Returns: TenantOverview[]
   */
  const tenantOverview = useQuery({
    queryKey: ['dashboard', 'tenants'],
    queryFn: adminDashboardService.getTenantOverview,
    refetchInterval: 60000, // 1 minute
  })

  /**
   * Query: Revenue metrics
   * Returns: RevenueMetrics
   */
  const revenueMetrics = useQuery({
    queryKey: ['dashboard', 'revenue'],
    queryFn: () => adminDashboardService.getRevenueMetrics('month'),
    refetchInterval: 300000, // 5 minutes
  })

  /**
   * Query: Resource metrics
   * Returns: ResourceMetrics
   */
  const resourceMetrics = useQuery({
    queryKey: ['dashboard', 'resources'],
    queryFn: adminDashboardService.getResourceMetrics,
    refetchInterval: 60000, // 1 minute
  })

  /**
   * Query: Recent activity
   * Returns: Activity[]
   */
  const recentActivity = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: () => adminDashboardService.getRecentActivity(10),
    refetchInterval: 30000, // 30 seconds
  })

  return {
    systemMetrics,
    tenantOverview,
    revenueMetrics,
    resourceMetrics,
    recentActivity,
  }
}
